/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { DelphinusCardFragment } from "@cosmos/hygraph-schema";

import {
	CosmosAsset,
	CosmosAvatar,
	CosmosCard,
	CosmosRichText,
	CosmosText,
	CosmosTextVariantEnum,
	EdsGradientType,
	EdsIcon,
	EdsIconType,
	TagList,
} from "@components/elements";
import { css, cx } from "@panda/css";
import { HStack, Stack, VStack } from "@panda/jsx";

const styles = css({
	height: "100%",
	maxW: "100%",
	overflow: "hidden",
});

const ContentStyles = css({
	alignItems: "start",
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	flexGrow: "1",
	minW: "0",
	height: "100%",
	maxW: "100%",
	gap: "eds.dimension.16",
});

const ThumbnailStyles = css({
	maxWidth: "180px",
	height: "auto",
	alignSelf: "stretch",
	flexShrink: "0",
	borderRadius: "eds.borderRadius.12",
	smDown: {
		maxWidth: "100%",
		aspectRatio: "2/1",
	},
});

const AssetStyles = css({
	height: "100%",
	width: "180px",
	alignSelf: "stretch",
	objectFit: "cover",
});

export function DelphinusCard({
	preHeading,
	icon,
	iconGradient,
	title,
	titleHref,
	desc,
	badge,
	avatar,
	assetPosition,
	asset,
}: DelphinusCardFragment): React.JSX.Element {
	return (
		<VStack alignItems="stretch" gap="eds.dimension.48" className={cx("delphinus-card-molecule")}>
			<CosmosCard
				className={cx("delphinus-card", styles)}
				padding={12}
				variant="material"
				direction="delphinusDir"
				assetPosition={assetPosition || undefined}
			>
				<CosmosCard.Content className={cx("gemini-card-content", ContentStyles)} padding={8}>
					<Stack
						justify="start"
						gap="eds.dimension.16"
						height="100%"
						maxW="100%"
						minW="0"
						className={cx("delphinus-card-content-text")}
					>
						<VStack className="ca" gap="eds.dimension.16" alignItems="flex-start">
							{preHeading || icon ? (
								<Stack className={cx("delphinus-card-pre-heading")}>
									{preHeading ? (
										<CosmosRichText
											html={preHeading}
											color="eds.text.secondary"
											variant="paragraphSM"
											className={css({ marginBottom: "-6px" })}
										/>
									) : null}
									<EdsIcon
										icon={icon as EdsIconType}
										size="xl"
										gradient={iconGradient as EdsGradientType}
										className={css({ marginBlockStart: "eds.dimension.8" })}
									/>
								</Stack>
							) : null}
							<CosmosCard.Link
								href={titleHref || ""}
								className={css({
									width: "100%",
									display: "flex",
									flexDirection: "column",
									textAlign: "start",
									gap: "eds.dimension.8",
								})}
							>
								{title ? (
									<CosmosText variant="headingSM" as="h3" color="eds.text.contrast">
										{title}
									</CosmosText>
								) : null}
								{desc ? (
									<CosmosRichText
										variant={CosmosTextVariantEnum.paragraphSM}
										color="eds.text.secondary"
										html={desc}
										className={css({
											overflowWrap: "anywhere",
											textOverflow: "ellipsis",
											lineClamp: "3",
											overflow: "hidden",
										})}
									/>
								) : null}
							</CosmosCard.Link>
						</VStack>
						{badge || avatar ? (
							<HStack className="delphinus-badge-avatar" gap="eds.dimension.16" alignItems="flex-start" flexWrap="wrap">
								{badge ? <TagList tags={[badge]}></TagList> : null}
								<CosmosAvatar {...avatar} />
							</HStack>
						) : null}
					</Stack>
				</CosmosCard.Content>
				{asset ? (
					<CosmosCard.Thumbnail className={ThumbnailStyles} aspectRatio="unset">
						<CosmosAsset asset={asset} className={AssetStyles} />
					</CosmosCard.Thumbnail>
				) : null}
			</CosmosCard>
		</VStack>
	);
}
